import { useUser } from '../../../Context/useUser';
import { Link, useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import { useState, useEffect, useMemo } from 'react';
import API from '../../../Utils/api';
import styles from './PerformancePage.module.css';

import { FaChartLine, FaPlus, FaColumns } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import PerformanceChart from './PerformanceChart';
import ReadinessChart from './ReadinessChart';
import PerformanceTable, { Glossary } from './PerformanceTable';
import { EARLIEST_DATE, isValidDateRange } from './readinessUtils';
import { getAcwrWarmupWindows } from './acwrWarmup';
import { buildChartDataSource, buildPerformanceSeries, prepareWorkouts, shortcutRange } from './performanceData';

// Empty State Component
const EmptyState = ({ currentUser, calendarPath }) => (
    <div className={styles.emptyState}>
        <FaChartLine className={styles.emptyStateIcon} />
        <h3 className={styles.emptyStateTitle}>No Performance Data Available</h3>
        <p className={styles.emptyStateMessage}>
            {currentUser?.firstName || 'The athlete'} hasn't completed any workouts yet. Performance
            analysis will be available once workouts are logged and completed.
        </p>
        <Link to={calendarPath} className={styles.emptyStateAction}>
            <FaPlus style={{ marginRight: 'var(--space-sm)' }} />
            Add Your First Workout
        </Link>
    </div>
);

export default function PerformancePage({
    showTable = true,
    showPerformanceChart = true,
    showReadinessChart = true,
    title = 'Performance Analysis',
    showTitle = true
}) {
    const { user } = useUser();
    const { athleteId } = useParams();
    const visitedUser = useVisitingUser(athleteId);
    const currentUser = athleteId ? visitedUser : user;
    // A coach visiting an athlete stays on that athlete's routes; there is no coach-level /calendar.
    const calendarPath = athleteId ? `/athlete/${athleteId}/calendar` : '/calendar';

    const [data, setData] = useState([]);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [showEmptyDays, setShowEmptyDays] = useState(true);
    const [showSideBySide, setShowSideBySide] = useState(false);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;
        const { data: performances } = await API.getAllUserWorkoutTypes(currentUser._id);
        setData(prepareWorkouts(performances));
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    useEffect(() => {
        handleShortcutClick('month');
    }, []);

    const { filteredData } = useMemo(() => buildPerformanceSeries(data, dateRange), [data, dateRange]);

    const chartDataSource = buildChartDataSource(filteredData, showEmptyDays);

    // ACWR warm-up windows come from the full workout history, never the selected range.
    const acwrWarmupWindows = useMemo(() => getAcwrWarmupWindows(data), [data]);

    // @George on purpose i did period and not months or days because we are able to utilize
    // the native Date API to set the dates because it is more accurate and easier to handle
    const handleShortcutClick = period => {
        setDateRange(shortcutRange(period));
    };

    // Check if there's any actual workout data
    const hasWorkoutData = data.length > 0;
    const hasFilteredData = filteredData.some(
        entry => entry.workoutCount > 0 || entry.isAssigned || entry.status === 'Assigned'
    );

    return (
        <div className={styles.pageWrap}>
            {showTitle && <h4 className={styles.title + ' island'}> {title}</h4>}

            {hasWorkoutData ? (
                <>
                    <div className={styles.dateFilterWrap + ' island'}>
                        <div className={styles.dateInputs}>
                            <div>
                                <label htmlFor='startDate'>Start Date:</label>
                                <input
                                    type='date'
                                    id='startDate'
                                    min={EARLIEST_DATE}
                                    value={dateRange.startDate}
                                    onChange={e => {
                                        setDateRange(prev => ({
                                            ...prev,
                                            startDate: e.target.value
                                        }));
                                    }}
                                />
                            </div>
                            <div>
                                <label htmlFor='endDate'>End Date:</label>
                                <input
                                    type='date'
                                    id='endDate'
                                    min={dateRange.startDate || EARLIEST_DATE}
                                    value={dateRange.endDate}
                                    onChange={e => {
                                        setDateRange(prev => ({
                                            ...prev,
                                            endDate: e.target.value
                                        }));
                                    }}
                                />
                            </div>
                            <div className={styles.shortcutButtons}>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('year')}
                                >
                                    1 Year
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('6months')}
                                >
                                    6 Months
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('month')}
                                >
                                    1 Month
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    onClick={() => handleShortcutClick('week')}
                                >
                                    1 Week
                                </button>
                                <button
                                    type='button'
                                    className={styles.eyeToggleBtn}
                                    title={showEmptyDays ? 'Hide empty days' : 'Show empty days'}
                                    onClick={() => setShowEmptyDays(v => !v)}
                                >
                                    {showEmptyDays ? <FaEye /> : <FaEyeSlash />}
                                </button>
                            </div>
                            {(dateRange.startDate || dateRange.endDate) &&
                                !isValidDateRange(dateRange.startDate, dateRange.endDate) && (
                                    <div className={styles.dateRangeError}>
                                        Please select a valid date range (end after start, from 2020 onwards)
                                    </div>
                                )}
                        </div>
                    </div>

                    {showTable && (
                        <div className={styles.dataTablesWrap + ' island'}>
                            {hasFilteredData ? (
                                <PerformanceTable
                                    data={data}
                                    dateRange={dateRange}
                                    isValidDateRange={isValidDateRange}
                                />
                            ) : (
                                <EmptyState currentUser={currentUser} calendarPath={calendarPath} />
                            )}
                        </div>
                    )}

                    {showPerformanceChart &&
                    showReadinessChart &&
                    hasFilteredData &&
                    showSideBySide ? (
                        <div className={styles.chartsWrap + ' island'}>
                            <button
                                type='button'
                                className={styles.sideBySideToggle}
                                title='Toggle side-by-side view'
                                onClick={() => setShowSideBySide(v => !v)}
                            >
                                <FaColumns />
                            </button>
                            <div className={styles.sideBySideCharts}>
                                <div className={styles.halfChart}>
                                    <PerformanceChart chartDataSource={chartDataSource} />
                                </div>
                                <div className={styles.halfChart}>
                                    <ReadinessChart
                                        chartDataSource={chartDataSource}
                                        filteredData={filteredData}
                                        acwrWarmupWindows={acwrWarmupWindows}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {(showPerformanceChart || showReadinessChart) && hasFilteredData && (
                                <div className={styles.chartsWrap + ' island'}>
                                    {showPerformanceChart && showReadinessChart && (
                                        <button
                                            type='button'
                                            className={styles.sideBySideToggle}
                                            title='Toggle side-by-side view'
                                            onClick={() => setShowSideBySide(v => !v)}
                                        >
                                            <FaColumns />
                                        </button>
                                    )}
                                    {showPerformanceChart && (
                                        <PerformanceChart chartDataSource={chartDataSource} />
                                    )}
                                    {showReadinessChart && (
                                        <ReadinessChart
                                            chartDataSource={chartDataSource}
                                            filteredData={filteredData}
                                            acwrWarmupWindows={acwrWarmupWindows}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {hasFilteredData && (
                        <div className='island' style={{ width: '100%' }}>
                            <Glossary />
                        </div>
                    )}
                </>
            ) : (
                <EmptyState currentUser={currentUser} calendarPath={calendarPath} />
            )}
        </div>
    );
}
